/* Copyright (c) 2022 MOCA Systems, Inc.  All rights reserved. */
import { getFileType } from '../../utils/file-utils';
import { dateTimeFormat } from '../../plain-javascript/background-scripts/fbdb-time';
import { fancyFirebaseReferencer } from '@touchplan/fancy-referencer';
import firebase from 'firebase/compat/app';
import { Attachment } from '../models/Attachment';
import dayjs from 'dayjs';

export interface IAttachmentMetadata {
	filename: string;
	mimeType: string;
	type?: string;
	clientFileName?: string;
	createdBy?: string;
	fileExtension?: string;
}

export enum ImageSize {
	Full = 'full',
	Preview = 'preview',
	Thumbnail = 'thumbnail',
}

export type TopLevelAttachmentPaths = 'projectAttachments' | 'ticketAttachments' | '';

export abstract class AbstractFileAttachApi {
	public readonly GCS_BUCKET = 'gcsBucket';
	public readonly GCS_BLOB = 'gcsBlob';
	public readonly THUMBNAIL_SUFFIX = '_100x100';
	public readonly PREVIEW_SUFFIX = '_300x300';

	protected isHighEfficiencyImage(file: File): boolean {
		// dev note. so normally we check file.type for extension, but for some reason file.type isn't correctly populated on window, so it fails the check
		const extension = file.name?.split('.')?.pop().toLowerCase();
		const extensionCheck = extension.indexOf('heic') > -1 || extension.indexOf('heif') > -1;
		const type = file.type.toLowerCase();
		const typeCheck = type.indexOf('heic') > -1 || type.indexOf('heif') > -1;
		return extensionCheck || typeCheck;
	}

	protected createAttachmentMetadata(
		userId: string,
		file: File,
		fileId?: string,
		filename?: string,
		fileType?: string
	): IAttachmentMetadata {
		return {
			type: !!fileId ? fileId : getFileType(fileType || file.type),
			createdBy: userId,
			createdAt: dayjs().format(dateTimeFormat), // this is temporary for local loading spinner, it will be replaced by timestamp returned by GCS
			mimeType: fileType || file.type,
			filename: filename || file.name,
		} as IAttachmentMetadata;
	}

	/** projectFilesRtdbRef should be pointed at `projectFiles/$projectId` */
	protected getCloudFilesPathFromRtdbRef(
		projectFilesRtdbRef: firebase.database.Reference,
		attachmentType: TopLevelAttachmentPaths,
		gcsFileId: string,
		suffix = ''
	) {
		//for legacy reasons, the projectFiles node and the gcs project files have the same path args, but in different order
		//sooo... "this"
		const projectId = projectFilesRtdbRef.key;
		const baseRef = projectFilesRtdbRef.parent; // `Root/projectFiles`
		// `Root/projectFiles/projectAttachments/$projectId` (unless legacy)
		const gcsDerivedRef =
			attachmentType === '' ? baseRef.child(projectId) : baseRef.child(attachmentType).child(projectId);

		const completeFileId = `${gcsFileId}${suffix}`;
		return fancyFirebaseReferencer.refToPath(gcsDerivedRef.child(completeFileId), true);
	}

	// BEGIN PLACEHOLDER REPLACE AS PART OF TP-5026
	protected createGcsBlobMetadata(userId: string, file: File, filename: string): IAttachmentMetadata {
		return {
			clientFileName: filename,
			createdBy: userId,
			fileExtension: filename,
			mimeType: file.type,
		} as IAttachmentMetadata;
	}
	// END PLACEHOLDER REPLACE AS PART OF TP-5026

	protected sortByTime(a: Attachment, b: Attachment): number {
		return new Date(b.view.createdAt).valueOf() - new Date(a.view.createdAt).valueOf();
	}

	protected isFileImage(file: File): boolean {
		return file.type.split('/').shift() === 'image';
	}
}
